import { useCallback, useEffect, useRef, useState } from 'react';
import {
    deleteAttachmentGroup,
    getAttachmentsByGroup,
    listAttachmentGroups,
} from '../../services/attachments/attachment.service';
import type { AttachmentGroupDto } from '../../services/attachments/attachment.types';

const getErrorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : String(error);

async function loadGroupAttachments(group: AttachmentGroupDto): Promise<AttachmentGroupDto> {
    const response = await getAttachmentsByGroup(group.id);
    if (!response.success || !response.data) {
        throw new Error(response.error?.message || `Failed to load attachments for "${group.profileName}"`);
    }
    return { ...group, attachments: response.data };
}

export function useAttachmentGroups() {
    const [groups, setGroups] = useState<AttachmentGroupDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionInFlight, setActionInFlight] = useState<number | null>(null);
    const requestId = useRef(0);

    const refresh = useCallback(async (suppressLoading = false) => {
        const currentRequest = ++requestId.current;
        if (!suppressLoading) setLoading(true);
        setError(null);
        try {
            const response = await listAttachmentGroups();
            if (!response.success || !response.data) {
                throw new Error(response.error?.message || 'Failed to load attachment groups');
            }
            const nextGroups = await Promise.all(response.data.map(loadGroupAttachments));
            if (currentRequest === requestId.current) setGroups(nextGroups);
        } catch (error: unknown) {
            if (currentRequest === requestId.current) setError(getErrorMessage(error));
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
        return () => { requestId.current += 1; };
    }, [refresh]);

    const loadForEdit = async (group: AttachmentGroupDto): Promise<AttachmentGroupDto | null> => {
        setError(null);
        try {
            return await loadGroupAttachments(group);
        } catch (error: unknown) {
            setError(getErrorMessage(error));
            return null;
        }
    };

    const remove = async (group: AttachmentGroupDto): Promise<boolean> => {
        const reasons: string[] = [];
        if (group.attachments?.length) reasons.push(`${group.attachments.length} attachment(s)`);
        if (group.siteRuleAttachmentGroups?.length) {
            reasons.push(`${group.siteRuleAttachmentGroups.length} site rule association(s)`);
        }
        if (reasons.length > 0) {
            setError(`Cannot delete "${group.profileName}" because it has ${reasons.join(' and ')}. Please remove these first.`);
            return false;
        }

        setActionInFlight(group.id);
        setError(null);
        try {
            const response = await deleteAttachmentGroup(group.id);
            if (!response.success) {
                const message = response.error?.message;
                if (message?.includes('entity changes') || message?.includes('constraint')) {
                    throw new Error(`Unable to delete "${group.profileName}". This is a server-side issue - please contact support or try again later. (Error: ${message})`);
                }
                throw new Error(message || 'Delete failed');
            }
            setGroups(previous => previous.filter(item => item.id !== group.id));
            return true;
        } catch (error: unknown) {
            setError(getErrorMessage(error));
            return false;
        } finally {
            setActionInFlight(null);
        }
    };

    return { groups, loading, error, actionInFlight, refresh, loadForEdit, remove, clearError: () => setError(null) };
}
